<script lang="ts">
	import { onMount } from "svelte";
	import { _, json } from "svelte-i18n";
	import { Button, Form, Spinner } from "@sveltestrap/sveltestrap";
	import type { NewPresentationSettings } from "../../../common/types";
	import { getV2Options } from "../../actions/getV2Options.ts";
	import { pushToast } from "../../store/toasts.ts";
	import { v2Options } from "../../store/v2Options.ts";
	import ImageGeneratorField from "./ImageGeneratorField.svelte";
	import PersonaField from "./PersonaField.svelte";
	import SlideCountField from "./SlideCountField.svelte";
	import SpeakerVideoGeneratorField from "./SpeakerVideoGeneratorField.svelte";
	import TextGeneratorField from "./TextGeneratorField.svelte";
	import TopicField from "./TopicField.svelte";
	import {
		CUSTOM_PERSONA_ID,
		type DraftCustomPersona,
		type SelectedPersona,
	} from "./customPersonaTypes.ts";
	import { buildPersonData } from "./customPersonaLogic.ts";
	import {
		loadRememberedSettings,
		saveRememberedSettings,
	} from "../../store/newPresentationV2Settings.ts";

	let {
		generatePresentation,
	}: { generatePresentation: (settings: NewPresentationSettings) => void } = $props();

	let topic = $state("");
	let numberOfSlides = $state(3);

	let selectedPersonaId = $state("");
	let customPersona = $state<DraftCustomPersona | null>(null);
	let selectedImageGenerator = $state("");
	let selectedSpeakerVideoGenerator = $state("");
	let selectedTextGenerator = $state("");
	let loadError = $state(false);

	onMount(async () => {
		try {
			await getV2Options();
		} catch (error) {
			loadError = true;
			pushToast({
				variant: "danger",
				message: $_("landingpage.newPresentation.loadingOptionsError"),
			});
			console.error("v2 options: failed to load", error);
		}
	});

	// Initialize selections once options arrive. The `!selectedPersonaId` guard
	// ensures this runs exactly once (on first load).
	$effect(() => {
		if ($v2Options && !selectedPersonaId) {
			const remembered = loadRememberedSettings();
			const opts = $v2Options;

			const rememberedSlides = remembered.numberOfSlides;
			if (typeof rememberedSlides === "number") {
				numberOfSlides = rememberedSlides;
			}

			const rememberedPersonaId = remembered.selectedPersonaId;
			if (
				typeof rememberedPersonaId === "string" &&
				rememberedPersonaId !== CUSTOM_PERSONA_ID &&
				opts.personas.some((p) => p.id === rememberedPersonaId)
			) {
				selectedPersonaId = rememberedPersonaId;
			} else {
				selectedPersonaId = opts.defaults.personaId;
			}

			const rememberedImage = remembered.selectedImageGenerator;
			if (
				typeof rememberedImage === "string" &&
				opts.imageGenerators.some((o) => o.value === rememberedImage)
			) {
				selectedImageGenerator = rememberedImage;
			} else {
				selectedImageGenerator = opts.defaults.imageGenerator;
			}

			const rememberedSpeakerVideo = remembered.selectedSpeakerVideoGenerator;
			if (
				typeof rememberedSpeakerVideo === "string" &&
				opts.speakerVideoGenerators.some((o) => o.value === rememberedSpeakerVideo)
			) {
				selectedSpeakerVideoGenerator = rememberedSpeakerVideo;
			} else {
				selectedSpeakerVideoGenerator = opts.defaults.speakerVideoGenerator;
			}

			const rememberedText = remembered.selectedTextGenerator;
			if (
				typeof rememberedText === "string" &&
				opts.textGenerators.some((o) => o.value === rememberedText)
			) {
				selectedTextGenerator = rememberedText;
			} else {
				selectedTextGenerator = opts.defaults.textGenerator;
			}
		}
	});

	const customSelectedWithoutDraft = $derived(
		selectedPersonaId === CUSTOM_PERSONA_ID && !customPersona,
	);

	const submitDisabled = $derived(
		!topic ||
			topic.trim().length < 3 ||
			!$v2Options ||
			!selectedPersonaId ||
			customSelectedWithoutDraft ||
			!selectedImageGenerator ||
			!selectedSpeakerVideoGenerator ||
			!selectedTextGenerator,
	);

	function getLanguageFromLocale() {
		return $json("languageNameForGeneration") as string;
	}

	function onSelectCustom(draft: DraftCustomPersona) {
		customPersona = draft;
	}

	function onDiscardCustom() {
		customPersona = null;
		if (selectedPersonaId === CUSTOM_PERSONA_ID) {
			selectedPersonaId = $v2Options?.defaults.personaId ?? "";
		}
	}

	function buildSettings(title: string): NewPresentationSettings {
		const opts = $v2Options;
		if (!opts) {
			throw new Error("v2 options not loaded");
		}

		// Resolve selected persona to a SelectedPersona discriminated union,
		// and collect base64 data URLs for image/audio if present. These
		// data URLs get passed via person.image/person.audio — the state
		// machine (createPresentation/logic/stateMachine.ts) uploads them
		// to /api/personData and replaces them with the resulting storage
		// paths before sending the WebSocket payload.
		let selected: SelectedPersona;
		const uploaded: { imageFile?: string; audioFile?: string } = {};

		if (selectedPersonaId === CUSTOM_PERSONA_ID) {
			if (!customPersona) {
				throw new Error("custom persona selected but no draft present");
			}
			selected = { kind: "custom", draft: customPersona };
			if (customPersona.imageBase64) {
				uploaded.imageFile = customPersona.imageBase64;
			}
			if (customPersona.voice.kind === "clone") {
				uploaded.audioFile = customPersona.voice.sampleBase64;
			}
		} else {
			const persona = opts.personas.find((p) => p.id === selectedPersonaId);
			if (!persona) {
				throw new Error("selected persona missing from options");
			}
			selected = { kind: "preset", persona };
		}

		return {
			title,
			numberOfSlides,
			narrationStyle: "",
			textGeneratorName: selectedTextGenerator,
			imageGeneratorName: selectedImageGenerator,
			speakerVideoGeneratorName: selectedSpeakerVideoGenerator,
			language: getLanguageFromLocale(),
			createCoverSlideVideo: false,
			createSubtitles: true,
			contentModeration: false,
			promotion: "none",
			generation: "v2",
			person: buildPersonData(selected, uploaded),
		};
	}

	function submit(event: Event) {
		event.preventDefault();
		event.stopPropagation();
		try {
			generatePresentation(buildSettings(topic.trim()));
			saveRememberedSettings({
				numberOfSlides,
				selectedPersonaId: selectedPersonaId === CUSTOM_PERSONA_ID ? undefined : selectedPersonaId,
				selectedImageGenerator,
				selectedSpeakerVideoGenerator,
				selectedTextGenerator,
			});
		} catch (error) {
			console.error("v2 form: submit failed", error);
			pushToast({
				variant: "danger",
				message: $_("landingpage.newPresentation.customPersona.uploadError"),
			});
		}
	}
</script>

<div class="wrapper">
	<Form on:submit={submit}>
		<TopicField bind:value={topic} />
		<SlideCountField bind:value={numberOfSlides} />

		{#if $v2Options}
			<PersonaField
				personas={$v2Options.personas}
				bind:value={selectedPersonaId}
				{customPersona}
				availableVoices={$v2Options.availableVoices}
				{onSelectCustom}
				{onDiscardCustom}
			/>
			<ImageGeneratorField
				options={$v2Options.imageGenerators}
				bind:value={selectedImageGenerator}
			/>
			<SpeakerVideoGeneratorField
				options={$v2Options.speakerVideoGenerators}
				bind:value={selectedSpeakerVideoGenerator}
			/>
			<TextGeneratorField options={$v2Options.textGenerators} bind:value={selectedTextGenerator} />
		{:else if !loadError}
			<div class="loading">
				<Spinner size="sm" />
				<span>{$_("landingpage.newPresentation.loadingOptions")}</span>
			</div>
		{/if}

		<Button type="submit" color="primary" disabled={submitDisabled}>
			{$_("landingpage.newPresentation.createPresentation")}
		</Button>
	</Form>
</div>

<style>
	.wrapper {
		max-width: 600px;
	}
	.loading {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-bottom: 1rem;
	}
</style>
